<template>
    <div class="min-h-screen bg-gray-100 overflow-x-hidden">
        <nav class="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 flex items-center gap-4 min-w-0">
            <router-link :to="{ name: 'adminDashboard', params: { uuid } }" class="text-gray-500 hover:text-gray-800 text-sm shrink-0">← Contas</router-link>
            <span class="text-gray-300 shrink-0">|</span>
            <span class="min-w-0 flex-1 text-sm text-gray-600 truncate">{{ company?.name || '...' }}</span>
        </nav>

        <div class="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
            <!-- Skeleton -->
            <div v-if="loading">
                <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
                    <div>
                        <div class="skeleton h-6 w-48 rounded mb-2"></div>
                        <div class="skeleton h-4 w-32 rounded"></div>
                    </div>
                </div>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div v-for="i in 4" :key="i" class="bg-white border border-gray-100 shadow-sm rounded-xl p-4">
                        <div class="skeleton h-3 w-16 rounded mb-2"></div>
                        <div class="skeleton h-7 w-12 rounded"></div>
                    </div>
                </div>
                <div class="skeleton h-32 w-full rounded-xl mb-6"></div>
                <div class="skeleton h-48 w-full rounded-xl"></div>
            </div>

            <div v-else-if="!company" class="text-red-600 text-sm">Conta não encontrada.</div>

            <div v-else>
                <!-- Header -->
                <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4 sm:mb-6 min-w-0">
                    <div>
                        <h1 class="text-base sm:text-xl font-bold text-gray-800">{{ company.name }}</h1>
                        <p class="text-gray-400 text-xs sm:text-sm">{{ company.email }}</p>
                    </div>
                    <button
                        @click="openToggleCompanyConfirm"
                        class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight whitespace-nowrap !bg-gray-900 hover:!bg-black !text-white !border-gray-900"
                    >
                        {{ company.status ? 'Desativar Empresa' : 'Ativar Empresa' }}
                    </button>
                </div>

                <!-- Metrics grid -->
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Usuários</div>
                        <div class="text-xl sm:text-2xl font-bold text-gray-800 break-words">{{ company.user_count }}</div>
                    </div>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Pacientes</div>
                        <div class="text-xl sm:text-2xl font-bold text-gray-800 break-words">{{ company.patient_count }}</div>
                    </div>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Avaliações</div>
                        <div class="text-xl sm:text-2xl font-bold text-gray-800 break-words">{{ company.record_count }}</div>
                    </div>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Tokens IA</div>
                        <div class="text-xl sm:text-2xl font-bold text-amber-600 break-all">{{ Number(company.ai_tokens).toLocaleString('pt-BR') }}</div>
                    </div>
                </div>

                <!-- Uso de IA (relatórios / ACIA) -->
                <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-5 mb-6">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <h2 class="text-sm font-semibold text-gray-500">Uso de IA</h2>
                        <button @click="openGrantCredit" class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight whitespace-nowrap self-start sm:self-auto">
                            + Adicionar créditos
                        </button>
                    </div>

                    <div v-if="loadingUsage" class="space-y-3">
                        <div class="skeleton h-4 w-full rounded"></div>
                        <div class="skeleton h-4 w-full rounded"></div>
                    </div>

                    <div v-else-if="usage" class="space-y-4">
                        <p class="text-xs text-gray-400">Plano <strong>{{ usage.plan }}</strong> — renova em {{ usageResetLabel }}</p>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <div class="flex items-center justify-between mb-1 text-sm">
                                    <span class="font-semibold text-gray-700">Relatórios com IA</span>
                                    <span class="text-gray-500">
                                        {{ usage.standard.used }} / {{ usage.standard.limit }}
                                        <span v-if="usage.standard.extraCredits > 0" class="text-emerald-600 font-semibold">(+{{ usage.standard.extraCredits }})</span>
                                    </span>
                                </div>
                                <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div class="h-full rounded-full bg-emerald-500" :style="{ width: usageBarWidth(usage.standard) + '%' }"></div>
                                </div>
                            </div>
                            <div>
                                <div class="flex items-center justify-between mb-1 text-sm">
                                    <span class="font-semibold text-gray-700">Análises ACIA</span>
                                    <span class="text-gray-500">
                                        {{ usage.acia.used }} / {{ usage.acia.limit }}
                                        <span v-if="usage.acia.extraCredits > 0" class="text-emerald-600 font-semibold">(+{{ usage.acia.extraCredits }})</span>
                                    </span>
                                </div>
                                <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div class="h-full rounded-full bg-emerald-500" :style="{ width: usageBarWidth(usage.acia) + '%' }"></div>
                                </div>
                            </div>
                        </div>

                        <details v-if="usage.grants && usage.grants.length" class="pt-2">
                            <summary class="cursor-pointer select-none text-xs font-semibold text-gray-500">Histórico de créditos concedidos</summary>
                            <div class="mt-2 divide-y divide-gray-100">
                                <div v-for="g in usage.grants" :key="g.uuid" class="py-2 text-xs text-gray-500 flex items-center justify-between gap-2">
                                    <span>
                                        <strong class="text-gray-700">+{{ g.amount }} {{ g.type === 'acia' ? 'ACIA' : 'padrão' }}</strong>
                                        ({{ g.source === 'courtesy' ? 'cortesia' : 'vendido' }})
                                        <span v-if="g.note"> — {{ g.note }}</span>
                                    </span>
                                    <span class="shrink-0 text-gray-400">{{ g.granted_by_admin_email }}</span>
                                </div>
                            </div>
                        </details>
                    </div>
                </div>

                <!-- AI Cost vs Revenue -->
                <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-5 mb-6">
                    <h2 class="text-sm font-semibold text-gray-500 mb-4">Custo IA vs Receita</h2>
                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                        <div>
                            <div class="text-xs text-gray-400 mb-1">Mensalidade</div>
                            <div class="text-lg font-bold text-emerald-600">{{ formatBrl(company.monthly_revenue_cents / 100) }}</div>
                        </div>
                        <div>
                            <div class="text-xs text-gray-400 mb-1">Custo IA (BRL est.)</div>
                            <div class="text-lg font-bold text-amber-600">{{ formatBrl(company.ai_cost_brl) }}</div>
                            <div class="text-xs text-gray-400">≈ ${{ Number(company.ai_cost_usd).toFixed(4) }} USD</div>
                        </div>
                        <div>
                            <div class="text-xs text-gray-400 mb-1">IA / Receita</div>
                            <div
                                class="text-lg font-bold"
                                :class="company.ai_cost_ratio > 80 ? 'text-red-600' : company.ai_cost_ratio > 40 ? 'text-orange-500' : 'text-emerald-600'"
                            >
                                {{ company.ai_cost_ratio }}%
                            </div>
                            <div class="mt-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    class="h-full rounded-full transition-all"
                                    :class="company.ai_cost_ratio > 80 ? 'bg-red-500' : company.ai_cost_ratio > 40 ? 'bg-orange-400' : 'bg-emerald-500'"
                                    :style="{ width: Math.min(company.ai_cost_ratio, 100) + '%' }"
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Users section -->
                <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-5">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <h2 class="text-sm font-semibold text-gray-500">Usuários</h2>
                        <button @click="openCreateUser" class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight whitespace-nowrap self-start sm:self-auto">
                            + Criar usuário
                        </button>
                    </div>

                    <!-- Loading skeleton -->
                    <div v-if="loadingUsers" class="space-y-2">
                        <div v-for="i in 3" :key="i" class="skeleton h-14 w-full rounded-lg"></div>
                    </div>

                    <template v-else>
                        <!-- Desktop table -->
                        <div class="hidden sm:block overflow-x-auto rounded-xl border border-gray-100">
                            <table class="table table-sm w-full min-w-[560px]">
                                <thead>
                                    <tr class="text-[11px] uppercase tracking-wide">
                                        <th class="text-left !text-black">Nome</th>
                                        <th class="text-left !text-black">Email</th>
                                        <th class="text-center !text-black">Role</th>
                                        <th class="text-center !text-black">Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="u in users" :key="u.id" class="hover:bg-gray-50 transition-colors">
                                        <td class="text-gray-700">{{ u.name }}</td>
                                        <td class="text-gray-400 text-xs">
                                            <div class="flex items-center gap-2">
                                                <span class="truncate">{{ u.email }}</span>
                                                <span
                                                    v-if="isGoogleUser(u)"
                                                    class="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600"
                                                    title="Conta criada com Google"
                                                >
                                                    <svg viewBox="0 0 24 24" class="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                                    </svg>
                                                    Google
                                                </span>
                                                <span v-if="u.invite_status === 'pending'" :class="adminBadgeClass('warning')">Pendente</span>
                                                <button
                                                    type="button"
                                                    class="text-gray-400 hover:text-cyan-600 transition-colors"
                                                    :title="copiedUserEmailId === u.id ? 'Email copiado' : 'Copiar email'"
                                                    :aria-label="copiedUserEmailId === u.id ? 'Email copiado' : 'Copiar email'"
                                                    @click="copyUserEmail(u)"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                        <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                        <td class="text-center">
                                            <span class="text-xs text-gray-500">{{ roleLabel(u.role) }}</span>
                                        </td>
                                        <td class="text-center">
                                            <span :class="adminBadgeClass(u.invite_status === 'pending' ? 'warning' : (u.status ? 'success' : 'error'))">
                                                {{ u.invite_status === 'pending' ? 'Pendente' : (u.status ? 'Ativo' : 'Inativo') }}
                                            </span>
                                        </td>
                                        <td class="text-right whitespace-nowrap">
                                            <button v-if="canResetPassword(u)" @click="openResetPassword(u)" class="text-xs text-cyan-600 hover:text-cyan-700 transition-colors mr-3">
                                                Resetar senha
                                            </button>
                                            <button @click="openToggleUserConfirm(u)" class="text-xs text-gray-400 hover:text-gray-800 transition-colors">
                                                {{ u.status ? 'Desativar' : 'Ativar' }}
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Mobile cards -->
                        <div class="sm:hidden space-y-3">
                            <div v-for="u in users" :key="u.id" class="border border-gray-100 rounded-xl p-4 min-w-0 overflow-hidden">
                                <div class="flex items-start justify-between gap-2 mb-2 min-w-0">
                                    <div class="min-w-0 flex-1">
                                        <div class="font-medium text-gray-700 truncate">{{ u.name }}</div>
                                        <div class="flex items-center gap-2 min-w-0">
                                            <div class="text-xs text-gray-400 truncate">{{ u.email }}</div>
                                            <span
                                                v-if="isGoogleUser(u)"
                                                class="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600"
                                                title="Conta criada com Google"
                                            >
                                                <svg viewBox="0 0 24 24" class="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                                </svg>
                                                Google
                                            </span>
                                            <span v-if="u.invite_status === 'pending'" :class="adminBadgeClass('warning')">Pendente</span>
                                            <button
                                                type="button"
                                                class="shrink-0 text-gray-400 hover:text-cyan-600 transition-colors"
                                                :title="copiedUserEmailId === u.id ? 'Email copiado' : 'Copiar email'"
                                                :aria-label="copiedUserEmailId === u.id ? 'Email copiado' : 'Copiar email'"
                                                @click="copyUserEmail(u)"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                    <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <span :class="adminBadgeClass(u.invite_status === 'pending' ? 'warning' : (u.status ? 'success' : 'error'))" class="shrink-0">
                                        {{ u.invite_status === 'pending' ? 'Pendente' : (u.status ? 'Ativo' : 'Inativo') }}
                                    </span>
                                </div>
                                <div class="text-xs text-gray-500 mb-3">{{ roleLabel(u.role) }}</div>
                                <div class="flex flex-col gap-2 pt-2 border-t border-gray-100 sm:flex-row sm:gap-4">
                                    <button v-if="canResetPassword(u)" @click="openResetPassword(u)" class="text-xs text-cyan-600 hover:text-cyan-700 transition-colors">
                                        Resetar senha
                                    </button>
                                    <button @click="openToggleUserConfirm(u)" class="text-xs text-gray-400 hover:text-gray-800 transition-colors">
                                        {{ u.status ? 'Desativar' : 'Ativar' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <!-- Todos os dados cadastrados -->
                <details class="bg-white border border-gray-100 shadow-sm rounded-xl mt-6 group">
                    <summary class="cursor-pointer select-none px-5 py-4 text-sm font-semibold text-gray-500 flex items-center justify-between">
                        Todos os dados cadastrados
                        <span class="text-gray-300 transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <div class="px-5 pb-5 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-gray-100 pt-4">
                        <div v-for="(value, key) in rawCompanyData" :key="key" class="border border-gray-100 rounded-lg p-3">
                            <div class="text-[11px] uppercase tracking-wide text-gray-400">{{ key }}</div>
                            <div class="text-sm text-gray-700 font-mono break-all">{{ value === null || value === undefined || value === '' ? '—' : value }}</div>
                        </div>
                    </div>
                </details>
            </div>
        </div>

        <!-- Grant usage credit modal -->
        <dialog ref="grantCreditModalRef" class="modal">
            <div class="modal-box w-11/12 max-w-lg">
                <h3 class="font-bold text-lg text-gray-800 mb-4">Adicionar créditos extras</h3>
                <div class="grid grid-cols-1 gap-3">
                    <div>
                        <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">Tipo</label>
                        <select v-model="grantForm.type" class="select select-bordered text-sm w-full">
                            <option value="standard">Relatório padrão</option>
                            <option value="acia">ACIA</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">Quantidade</label>
                        <input v-model.number="grantForm.amount" type="number" min="1" class="input input-bordered text-sm w-full" />
                    </div>
                    <div>
                        <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">Origem</label>
                        <select v-model="grantForm.source" class="select select-bordered text-sm w-full">
                            <option value="courtesy">Cortesia</option>
                            <option value="purchased">Vendido</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">Nota (opcional)</label>
                        <input v-model="grantForm.note" type="text" class="input input-bordered text-sm w-full" />
                    </div>
                </div>
                <p v-if="grantError" class="text-red-600 text-xs mt-3">{{ grantError }}</p>
                <div class="modal-action flex-col sm:flex-row">
                    <button class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" @click="closeGrantCredit">Cancelar</button>
                    <button
                        @click="confirmGrantCredit"
                        :disabled="granting || !grantForm.amount || grantForm.amount <= 0"
                        class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight whitespace-nowrap"
                    >
                        {{ granting ? 'Adicionando...' : 'Adicionar' }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop"><button>close</button></form>
        </dialog>

        <!-- Toggle company confirm modal -->
        <dialog ref="toggleCompanyModalRef" class="modal">
            <div class="modal-box w-11/12 max-w-lg" v-if="company">
                <h3 class="font-bold text-lg text-gray-800">
                    {{ company.status ? 'Desativar empresa?' : 'Ativar empresa?' }}
                </h3>
                <p class="py-3 text-sm text-gray-600" v-if="company.status">
                    Isso vai: bloquear login de todo mundo dessa empresa, desativar todos os usuários (inclusive o admin/dono), e
                    <strong>derrubar na hora quem já estiver logado</strong> — não espera o token expirar. Nada é apagado, é reversível pelo mesmo botão.
                </p>
                <p class="py-3 text-sm text-gray-600" v-else>
                    Isso vai reativar a empresa e todos os seus usuários, permitindo login novamente.
                </p>
                <div class="modal-action flex-col sm:flex-row">
                    <button class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" @click="closeToggleCompanyModal">Cancelar</button>
                    <button
                        class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight !text-white"
                        :class="company.status ? '!bg-red-600 hover:!bg-red-700' : '!bg-emerald-600 hover:!bg-emerald-700'"
                        @click="confirmToggleCompany"
                    >
                        {{ company.status ? 'Desativar' : 'Ativar' }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop"><button>close</button></form>
        </dialog>

        <!-- Create user modal -->
        <dialog ref="createUserModalRef" class="modal">
            <div class="modal-box w-11/12 max-w-lg">
                <h3 class="font-bold text-lg text-gray-800 mb-4">Novo usuário</h3>
                <div class="grid grid-cols-1 gap-3">
                    <input v-model="newUser.name" type="text" placeholder="Nome" class="input input-bordered text-sm" />
                    <input v-model="newUser.email" type="email" placeholder="Email" class="input input-bordered text-sm" />
                    <div>
                        <div class="flex flex-col gap-2 sm:flex-row">
                            <input
                                v-model="newUser.password"
                                :type="showNewUserPassword ? 'text' : 'password'"
                                placeholder="Senha temporária (mín. 12 caracteres)"
                                class="input input-bordered text-sm flex-1 min-w-0 font-mono"
                            />
                            <button type="button" class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight whitespace-nowrap" @click="showNewUserPassword = !showNewUserPassword">
                                {{ showNewUserPassword ? 'Ocultar' : 'Ver' }}
                            </button>
                            <button type="button" class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight whitespace-nowrap" @click="generateNewUserPassword">
                                Gerar senha
                            </button>
                        </div>
                        <p v-if="newUser.password && newUserPasswordError" class="text-red-600 text-xs mt-1">{{ newUserPasswordError }}</p>
                        <p v-else class="text-gray-400 text-xs mt-1">Mínimo 12 caracteres, com maiúscula, minúscula, número e símbolo.</p>
                    </div>
                    <select v-model="newUser.role" class="select select-bordered text-sm">
                        <option value="member">Membro</option>
                        <option value="admin">Admin</option>
                        <option value="owner">Admin (dono)</option>
                    </select>
                    <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input type="checkbox" v-model="newUser.send_email" class="checkbox checkbox-sm" />
                        Enviar senha por email também?
                    </label>
                </div>
                <p v-if="createError" class="text-red-600 text-xs mt-3">{{ createError }}</p>
                <div class="modal-action flex-col sm:flex-row">
                    <button class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" @click="closeCreateUserModal">Cancelar</button>
                    <button
                        @click="createUser"
                        :disabled="creating || !!newUserPasswordError || !newUser.password"
                        class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight whitespace-nowrap"
                    >
                        {{ creating ? 'Criando...' : 'Criar' }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop"><button>close</button></form>
        </dialog>

        <!-- Deactivate/activate user confirm modal -->
        <dialog ref="toggleUserModalRef" class="modal">
            <div class="modal-box w-11/12 max-w-lg" v-if="toggleUserTarget">
                <h3 class="font-bold text-lg text-gray-800">
                    {{ toggleUserTarget.status ? 'Desativar usuário?' : 'Ativar usuário?' }}
                </h3>
                <p class="py-3 text-sm text-gray-600">
                    <template v-if="toggleUserTarget.status">
                        <strong>{{ toggleUserTarget.name }}</strong> não vai conseguir fazer login nem acessar o sistema depois de desativado.
                    </template>
                    <template v-else>
                        <strong>{{ toggleUserTarget.name }}</strong> volta a conseguir fazer login normalmente.
                    </template>
                </p>
                <div class="modal-action flex-col sm:flex-row">
                    <button class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" @click="closeToggleUserModal">Cancelar</button>
                    <button
                        class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight !text-white"
                        :class="toggleUserTarget.status ? '!bg-red-600 hover:!bg-red-700' : '!bg-emerald-600 hover:!bg-emerald-700'"
                        @click="confirmToggleUserStatus"
                    >
                        {{ toggleUserTarget.status ? 'Desativar' : 'Ativar' }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop"><button>close</button></form>
        </dialog>

        <!-- Reset password modal -->
        <dialog ref="resetPasswordModalRef" class="modal">
            <div class="modal-box w-11/12 max-w-lg" v-if="resetPasswordTarget">
                <h3 class="font-bold text-lg text-gray-800">Resetar senha de {{ resetPasswordTarget.name }}</h3>

                <div class="py-3">
                    <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">Nova senha</label>
                    <div class="flex flex-col gap-2 sm:flex-row">
                        <input :value="resetPasswordValue" readonly class="input input-bordered text-sm flex-1 min-w-0 font-mono" />
                        <button type="button" class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight whitespace-nowrap" @click="regenerateResetPassword">
                            Gerar nova
                        </button>
                        <button type="button" class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight whitespace-nowrap" @click="copyResetPassword">
                            {{ resetPasswordCopied ? 'Copiado!' : 'Copiar' }}
                        </button>
                    </div>

                    <label class="flex items-center gap-2 mt-4 text-sm text-gray-600 cursor-pointer">
                        <input type="checkbox" v-model="resetPasswordSendEmail" class="checkbox checkbox-sm" />
                        Enviar por email também?
                    </label>

                    <div v-if="resetPasswordDone" class="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">
                        Senha redefinida com sucesso.
                        <span v-if="resetPasswordSendEmail"> {{ resetPasswordEmailSent ? 'Email enviado.' : 'Não foi possível enviar o email.' }}</span>
                    </div>
                    <p v-if="resetPasswordError" class="text-red-600 text-xs mt-2">{{ resetPasswordError }}</p>
                </div>

                <div class="modal-action flex-col sm:flex-row">
                    <button class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" @click="closeResetPasswordModal">
                        {{ resetPasswordDone ? 'Fechar' : 'Cancelar' }}
                    </button>
                    <button
                        v-if="!resetPasswordDone"
                        class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight"
                        :disabled="resettingPassword"
                        @click="confirmResetPassword"
                    >
                        {{ resettingPassword ? 'Confirmando...' : 'Confirmar' }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop"><button>close</button></form>
        </dialog>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import AdminCompanyService from '@/services/admin/AdminCompanyService';
import { adminBadgeClass } from '@/constants/adminBadge';
import { generateSecurePassword, validatePasswordStrength } from '@/utils/password';

const ROLE_LABELS = { owner: 'Admin (dono)', admin: 'Admin', member: 'Membro' };

export default {
    name: 'AdminCompanyDetail',
    setup() {
        const route = useRoute();
        const toast = useToast();
        const companyId = computed(() => route.params.id);
        const uuid = computed(() => route.params.uuid);

        const company = ref(null);
        const rawCompanyData = computed(() => company.value || {});
        const users = ref([]);
        const loading = ref(true);
        const loadingUsers = ref(true);

        const createUserModalRef = ref(null);
        const creating = ref(false);
        const createError = ref('');
        const newUser = ref({ name: '', email: '', password: '', role: 'member', send_email: false });
        const showNewUserPassword = ref(false);
        const newUserPasswordError = computed(() => validatePasswordStrength(newUser.value.password));

        const usage = ref(null);
        const loadingUsage = ref(true);
        const grantCreditModalRef = ref(null);
        const grantForm = ref({ type: 'standard', amount: 1, source: 'courtesy', note: '' });
        const granting = ref(false);
        const grantError = ref('');

        const toggleCompanyModalRef = ref(null);

        const toggleUserModalRef = ref(null);
        const toggleUserTarget = ref(null);

        const resetPasswordModalRef = ref(null);
        const resetPasswordTarget = ref(null);
        const resetPasswordValue = ref('');
        const resetPasswordSendEmail = ref(false);
        const resetPasswordDone = ref(false);
        const resetPasswordEmailSent = ref(false);
        const resetPasswordError = ref('');
        const resetPasswordCopied = ref(false);
        const resettingPassword = ref(false);
        const copiedUserEmailId = ref(null);

        onMounted(async () => {
            await Promise.all([loadCompany(), loadUsers(), loadUsage()]);
        });

        async function loadUsage() {
            loadingUsage.value = true;
            try {
                const { data } = await AdminCompanyService.getUsage(companyId.value);
                usage.value = data.data;
            } finally {
                loadingUsage.value = false;
            }
        }

        function usageBarWidth(bucket) {
            const total = bucket.limit + bucket.extraCredits;
            if (!total) return 0;
            return Math.min((bucket.used / total) * 100, 100);
        }

        const usageResetLabel = computed(() => {
            if (!usage.value || !usage.value.period || !usage.value.period.start) return '—';
            const start = new Date(usage.value.period.start);
            const nextMonth = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
            return nextMonth.toLocaleDateString('pt-BR');
        });

        function openGrantCredit() {
            grantForm.value = { type: 'standard', amount: 1, source: 'courtesy', note: '' };
            grantError.value = '';
            grantCreditModalRef.value?.showModal();
        }

        function closeGrantCredit() {
            grantCreditModalRef.value?.close();
        }

        async function confirmGrantCredit() {
            grantError.value = '';
            granting.value = true;
            try {
                await AdminCompanyService.grantUsageCredit(companyId.value, grantForm.value);
                await loadUsage();
                closeGrantCredit();
            } catch (err) {
                grantError.value = err.response?.data?.error || 'Erro ao adicionar créditos';
            } finally {
                granting.value = false;
            }
        }

        async function loadCompany() {
            try {
                const { data } = await AdminCompanyService.getById(companyId.value);
                company.value = data.data;
            } finally {
                loading.value = false;
            }
        }

        async function loadUsers() {
            try {
                const { data } = await AdminCompanyService.getUsers(companyId.value);
                users.value = data.data;
            } finally {
                loadingUsers.value = false;
            }
        }

        function roleLabel(role) {
            return ROLE_LABELS[role] || role;
        }

        function isGoogleUser(user) {
            return !!(user && (user.auth_provider === 'google' || user.is_google_user));
        }

        function canResetPassword(user) {
            return !!(user && !isGoogleUser(user) && user.invite_status !== 'pending');
        }

        function openToggleCompanyConfirm() {
            toggleCompanyModalRef.value?.showModal();
        }

        function closeToggleCompanyModal() {
            toggleCompanyModalRef.value?.close();
        }

        async function confirmToggleCompany() {
            const { data } = await AdminCompanyService.toggleStatus(companyId.value);
            const newStatus = data && data.data ? data.data.status : !company.value.status;
            company.value.status = newStatus;
            users.value.forEach(u => { u.status = newStatus; });
            closeToggleCompanyModal();
        }

        function openCreateUser() {
            newUser.value = { name: '', email: '', password: '', role: 'member', send_email: false };
            showNewUserPassword.value = false;
            createError.value = '';
            createUserModalRef.value?.showModal();
        }

        function closeCreateUserModal() {
            createUserModalRef.value?.close();
        }

        function generateNewUserPassword() {
            newUser.value.password = generateSecurePassword();
            showNewUserPassword.value = true;
        }

        async function createUser() {
            createError.value = '';
            if (!newUser.value.name || !newUser.value.email || !newUser.value.password) {
                createError.value = 'Preencha todos os campos';
                return;
            }
            if (newUserPasswordError.value) {
                createError.value = newUserPasswordError.value;
                return;
            }
            creating.value = true;
            try {
                const { data } = await AdminCompanyService.createUser(companyId.value, newUser.value);
                users.value.push({ ...data.data, status: true });
                closeCreateUserModal();
            } catch (err) {
                createError.value = err.response?.data?.text || 'Erro ao criar usuário';
            } finally {
                creating.value = false;
            }
        }

        function openToggleUserConfirm(user) {
            toggleUserTarget.value = user;
            toggleUserModalRef.value?.showModal();
        }

        function closeToggleUserModal() {
            toggleUserModalRef.value?.close();
            toggleUserTarget.value = null;
        }

        async function confirmToggleUserStatus() {
            const user = toggleUserTarget.value;
            if (!user) return;
            await AdminCompanyService.toggleUserStatus(companyId.value, user.id);
            user.status = !user.status;
            closeToggleUserModal();
        }

        function openResetPassword(user) {
            resetPasswordTarget.value = user;
            resetPasswordValue.value = generateSecurePassword();
            resetPasswordSendEmail.value = false;
            resetPasswordDone.value = false;
            resetPasswordEmailSent.value = false;
            resetPasswordError.value = '';
            resetPasswordCopied.value = false;
            resetPasswordModalRef.value?.showModal();
        }

        function regenerateResetPassword() {
            resetPasswordValue.value = generateSecurePassword();
            resetPasswordCopied.value = false;
        }

        async function copyResetPassword() {
            try {
                await navigator.clipboard.writeText(resetPasswordValue.value);
                resetPasswordCopied.value = true;
                setTimeout(() => { resetPasswordCopied.value = false; }, 2000);
            } catch (e) { /* clipboard indisponível */ }
        }

        async function copyUserEmail(user) {
            if (!user || !user.email) return;
            try {
                await navigator.clipboard.writeText(user.email);
                copiedUserEmailId.value = user.id;
                toast.open({ message: 'Email copiado.', type: 'success', position: 'top-right' });
                setTimeout(() => {
                    if (copiedUserEmailId.value === user.id) copiedUserEmailId.value = null;
                }, 2000);
            } catch (e) {
                toast.open({ message: 'Não foi possível copiar o email.', type: 'error', position: 'top-right' });
            }
        }

        function closeResetPasswordModal() {
            resetPasswordModalRef.value?.close();
            resetPasswordTarget.value = null;
        }

        async function confirmResetPassword() {
            const user = resetPasswordTarget.value;
            if (!user) return;
            resetPasswordError.value = '';
            resettingPassword.value = true;
            try {
                const { data } = await AdminCompanyService.resetUserPassword(companyId.value, user.id, {
                    password: resetPasswordValue.value,
                    send_email: resetPasswordSendEmail.value
                });
                resetPasswordDone.value = true;
                resetPasswordEmailSent.value = !!(data && data.data && data.data.email_sent);
            } catch (err) {
                resetPasswordError.value = err.response?.data?.text || 'Erro ao redefinir senha';
            } finally {
                resettingPassword.value = false;
            }
        }

        function formatBrl(value) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
        }

        return {
            uuid, company, rawCompanyData, users, loading, loadingUsers,
            usage, loadingUsage, usageBarWidth, usageResetLabel,
            grantCreditModalRef, grantForm, granting, grantError,
            openGrantCredit, closeGrantCredit, confirmGrantCredit,
            createUserModalRef, creating, createError, newUser, showNewUserPassword, newUserPasswordError,
            openCreateUser, closeCreateUserModal, generateNewUserPassword, createUser,
            roleLabel, formatBrl, adminBadgeClass,
            toggleCompanyModalRef, openToggleCompanyConfirm, closeToggleCompanyModal, confirmToggleCompany,
            toggleUserModalRef, toggleUserTarget, openToggleUserConfirm, closeToggleUserModal, confirmToggleUserStatus,
            resetPasswordModalRef, resetPasswordTarget, resetPasswordValue, resetPasswordSendEmail,
            resetPasswordDone, resetPasswordEmailSent, resetPasswordError, resetPasswordCopied, resettingPassword,
            openResetPassword, regenerateResetPassword, copyResetPassword, closeResetPasswordModal, confirmResetPassword,
            copiedUserEmailId, copyUserEmail,
            isGoogleUser, canResetPassword
        };
    }
};
</script>
